import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowRight, Target, ShieldCheck, PenLine, Layers, RadioTower, Gem, Upload,
  Check, ChevronDown, ExternalLink,
} from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1742279157096-b7739f394cf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNyeXN0YWwlMjBkYXJrJTIwZ29sZCUyMHJlZnJhY3Rpb258ZW58MHx8fHwxNzgxNjU1NDU5fDA&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: ShieldCheck,
    title: "Real nonprofit leads — no fiction",
    body: "Every prospect is a US-registered nonprofit sourced from ProPublica's IRS 990 filings. You get the org name, EIN, latest revenue, filing year, and NTEE category. No fabricated people, no invented emails.",
  },
  {
    icon: PenLine,
    title: "One-click grounded outreach drafts",
    body: "Open any real lead and get a personalized email opener from Claude — strictly grounded in the org's 990 data. The prompt is hard-locked against inventing recipient names, missions, or programs.",
  },
  {
    icon: Layers,
    title: "Batch draft in parallel",
    body: "Select up to 10 real leads, click Batch draft, and get all openers in about 7 seconds. Export the whole set as CSV or save straight into a campaign — one demo goes from mining to sent-ready copy.",
  },
  {
    icon: RadioTower,
    title: "Truthful live signal feed",
    body: "The dashboard shows only data-driven signals: real matches added, pipeline pending, campaigns active. When your workspace is empty, example signals are clearly labeled — no fake urgency.",
  },
  {
    icon: Target,
    title: "ICP-first filtering",
    body: "Set your industry, role, geography (US state code), and keywords once. Every mine + every signal respects it. Change your ICP, refine your list.",
  },
  {
    icon: Upload,
    title: "Upload your own list",
    body: "CSV or Excel up to 500 rows. Map columns to fields (Name, Email, Company, Subject, Body…), preview the first 10 rows, then commit. Undo the entire import in one click within 24 hours.",
  },
];

const faqs = [
  {
    q: "Where do the leads actually come from?",
    a: "Real leads come from ProPublica's Nonprofit Explorer, which surfaces IRS Form 990 filings for US-registered nonprofits. You get the organization's official name, EIN, latest reported revenue, filing year, city/state, and NTEE (activity) code. We do not scrape LinkedIn or fabricate contact emails.",
  },
  {
    q: "Do you provide individual contact emails or names?",
    a: "No — and we're honest about it. ProPublica exposes the organization, not staff. Each real lead ships with a 'Contact TBD' placeholder so you can research the right person on the org's official site. Contact enrichment via Apollo / Hunter is on the roadmap.",
  },
  {
    q: "How is the outreach draft not just hallucinated fluff?",
    a: "The Claude prompt is constrained to a small list of verified facts (org name, city/state, EIN, NTEE category, latest revenue + filing year). The system message explicitly forbids inventing recipient names, missions, programs, or staff. Every draft ships with a 'what was and wasn't verified' disclaimer.",
  },
  {
    q: "Can I upload my own contact list?",
    a: "Yes — CSV or Excel, up to 500 rows per upload. You map any header row to target fields (Name, Email, Company, Title, Subject, Body, and more), preview the first 10 rows, and commit. After import you get a report of any per-row rejections (invalid emails, missing name+company). You can undo the entire import in one click for up to 24 hours.",
  },
  {
    q: "Where does my uploaded file go?",
    a: "Your file is parsed in your browser using SheetJS — the raw spreadsheet never leaves your device. Only the mapped fields for the rows you commit are POSTed to our server. See Privacy Policy §2B for the full storage and use breakdown.",
  },
  {
    q: "Am I allowed to email the contacts I upload?",
    a: "You are the sender of any outreach. You are responsible for ensuring you have permission or a legitimate basis to contact each individual under CAN-SPAM (US), CASL (Canada), GDPR/PECR (EU/UK), or any other applicable rule. Full obligations are described in Privacy Policy §2B.",
  },
  {
    q: "Is My Leads Convert free?",
    a: "During the MVP it's free to sign up and use — no card, no trial gimmick. Both ProPublica sourcing and CSV/Excel uploads are free, with no API key or paid tier required. Drafting uses Claude via an Emergent-managed key. There's no pricing page or plan gating right now. Note: a Demo-mode toggle in Settings lets you optionally add AI-generated sample leads for pitch decks — they're clearly badged 'AI demo' and never mix with real records.",
  },
  {
    q: "Can I export drafts?",
    a: "Yes. From the batch draft modal you can download every draft as a CSV (company + subject + body + disclaimer + generated_at) or click 'Save as campaign' to push them into the built-in Campaigns workspace.",
  },
];

const SectionLabel = ({ children }) => (
  <div className="inline-flex items-center gap-2 mb-6">
    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
    <span className="label-eyebrow">{children}</span>
  </div>
);

const Hero = () => {
  const [preview, setPreview] = useState([]);
  useEffect(() => {
    api.get("/preview/leads").then(({ data }) => setPreview(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="relative pt-40 pb-24 px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 80%)",
        }} />
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse 50% 40% at 50% 30%, rgba(52,211,153,0.12), transparent 60%)"
        }} />
        <div className="noise-overlay" />
      </div>

      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center mb-8 fade-up">
          <div className="glass-card px-4 py-1.5 inline-flex items-center gap-2 text-xs text-zinc-300">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
            <span>Real IRS-verified leads + your own uploads · MVP · early access</span>
          </div>
        </div>

        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter text-white text-center max-w-5xl mx-auto leading-[1.02] fade-up" style={{ animationDelay: "60ms" }}>
          Real nonprofits.{" "}
          <span className="gradient-text">Grounded outreach.</span>
          <br />
          Zero fabricated contacts.
        </h1>

        <div className="mt-4 text-center fade-up" style={{ animationDelay: "100ms" }} data-testid="hero-sub-line">
          <span className="text-base sm:text-lg text-emerald-300/90 font-medium">Bring your own list or mine ours.</span>
        </div>

        <p className="mt-6 text-lg sm:text-xl text-zinc-400 text-center max-w-2xl mx-auto leading-relaxed fade-up" style={{ animationDelay: "140ms" }}>
          My Leads Convert pulls US-registered nonprofits straight from ProPublica's 990 filings, then drafts personalized outreach openers with Claude — strictly grounded in the org's public facts. No invented people, no inflated stats.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 fade-up" style={{ animationDelay: "220ms" }}>
          <Link to="/register" className="btn-primary" data-testid="hero-cta-primary">
            Create your workspace <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#how" className="btn-ghost" data-testid="hero-cta-secondary">
            See how it works
          </a>
        </div>

        <div className="mt-6 flex items-center justify-center gap-6 text-xs text-zinc-500 fade-up flex-wrap" style={{ animationDelay: "300ms" }}>
          <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" /> Free during MVP</span>
          <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" /> Real 990 data, not scraped</span>
          <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" /> No card required</span>
        </div>

        {/* Live preview card — clearly labeled as an example */}
        <div className="mt-20 fade-up" style={{ animationDelay: "400ms" }}>
          <div className="glass-panel rounded-3xl p-2 max-w-5xl mx-auto">
            <div className="rounded-2xl bg-black/60 p-6 lg:p-8">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="label-eyebrow text-emerald-300">Example workspace preview</span>
                </div>
                <span className="text-xs text-zinc-500 font-mono">Illustrative — not live data</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(preview.length ? preview : Array(6).fill(null)).map((lead, i) => (
                  <div key={i} className="glass-card glass-card-hover p-4">
                    {lead ? (
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-medium text-white text-sm">{lead.name}</div>
                            <div className="text-xs text-zinc-500">{lead.title}</div>
                          </div>
                          <div className="text-xs font-mono text-gold">{lead.score}</div>
                        </div>
                        <div className="text-sm text-zinc-300">{lead.company}</div>
                        <div className="text-xs text-zinc-500 mt-1">{lead.industry} · {lead.location}</div>
                      </>
                    ) : (
                      <div className="h-16 shimmer rounded" />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
                Layout preview only. Real records inside the dashboard carry a green "Real" badge, an EIN, and a link to their ProPublica public record.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      title: "Set your ICP",
      body: "Pick an industry (Nonprofit is where real sourcing lives today), a role, a US state or geography, and a few keywords. Takes about 30 seconds.",
    },
    {
      n: "02",
      title: "Mine real leads or upload your own list.",
      body: "Click Mine real leads to pull verified nonprofits from ProPublica's 990 filings, or click Upload CSV / Excel to bring in your own contacts. Either path fills your workspace in seconds.",
    },
    {
      n: "03",
      title: "Draft outreach — one or ten",
      body: "Open a lead for a single grounded opener, or select up to 10 and click Batch draft. Export CSV or save straight to a campaign. Every draft ships with a verification disclaimer.",
    },
  ];
  return (
    <section id="how" className="relative py-32 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <SectionLabel>The process</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-white">
            Three steps. <span className="text-zinc-500">No spreadsheets, no scraping.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="glass-card glass-card-hover p-8 relative overflow-hidden">
              <div className="absolute -top-6 -right-4 text-[7rem] font-display font-medium text-white/[0.03] leading-none">{s.n}</div>
              <div className="text-emerald-300 text-sm font-mono mb-4">{s.n}</div>
              <h3 className="font-display text-2xl text-white mb-3">{s.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Features = () => (
  <section id="features" className="relative py-32 px-6 lg:px-8">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
        <div className="lg:col-span-2">
          <SectionLabel>What's inside</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-white max-w-2xl">
            An honest prospecting agent, <span className="text-emerald-300">built for nonprofits first.</span>
          </h2>
        </div>
        <p className="text-zinc-400 self-end">
          We chose to source real, public 990 data instead of inventing prospects. Every feature is designed around that constraint — real records, grounded drafts, truthful signals.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((f, i) => (
          <div key={i} className="glass-card glass-card-hover p-7 group" data-testid={`feature-card-${i}`}>
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-300/10 to-emerald-500/5 border border-emerald-300/20 flex items-center justify-center mb-5 group-hover:from-emerald-300/20 transition-colors">
              <f.icon className="w-5 h-5 text-emerald-300" />
            </div>
            <h3 className="font-display text-xl text-white mb-2">{f.title}</h3>
            <p className="text-zinc-400 leading-relaxed text-[0.95rem]">{f.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DataSource = () => (
  <section className="relative py-24 px-6 lg:px-8">
    <div className="max-w-5xl mx-auto glass-panel rounded-3xl p-10 lg:p-14">
      <div className="flex flex-col md:flex-row items-start gap-8">
        <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
          <ShieldCheck className="w-7 h-7 text-emerald-300" />
        </div>
        <div>
          <SectionLabel>Where the data comes from</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-medium text-white mb-4 tracking-tight">
            Every real lead traces back to a public 990 filing.
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-4">
            We source real nonprofit records from <span className="text-white">ProPublica's Nonprofit Explorer</span>, a public
            index of IRS Form 990 filings for US-registered 501(c) organizations. When you mine a lead you get its official name,
            EIN, city/state, latest reported revenue and filing year, and NTEE activity category.
          </p>
          <p className="text-zinc-500 text-sm leading-relaxed mb-6">
            No individual staff names, emails, or LinkedIn URLs are fabricated. Where those fields are blank, they stay blank until you research them yourself.
          </p>
          <a href="https://projects.propublica.org/nonprofits/" target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="propublica-link">
            View ProPublica Nonprofit Explorer <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>

    <div className="max-w-5xl mx-auto mt-8 glass-card p-6 lg:p-8 border-blue-500/25 bg-blue-500/[0.04]" data-testid="upload-callout">
      <div className="flex flex-col md:flex-row items-start gap-5">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
          <Upload className="w-5 h-5 text-blue-300" />
        </div>
        <div className="flex-1">
          <div className="label-eyebrow mb-1 text-blue-200/80">Have your own list?</div>
          <h3 className="font-display text-xl sm:text-2xl text-white mb-3 tracking-tight">Bringing your own list?</h3>
          <p className="text-zinc-300 leading-relaxed text-sm mb-3">
            Upload a CSV or Excel spreadsheet and we'll parse it{" "}
            <span className="text-white">in your browser</span> — the raw file never leaves your device. Only the fields you map for the rows you commit are stored in your workspace. You can undo the entire import in one click within <span className="text-white">24 hours</span>, and delete anything individually after that.
          </p>
          <p className="text-xs text-amber-200/90 leading-relaxed mb-4">
            <span className="text-amber-300 font-medium">You are the sender.</span> Please ensure you have permission or a legitimate basis to email any contacts you upload (CAN-SPAM / GDPR / your local rules).
          </p>
          <Link to="/privacy#2B" className="btn-ghost text-xs" data-testid="upload-callout-privacy">
            Read Privacy Policy §2B <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-32 px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <SectionLabel>Questions, answered</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-white">
            Straight answers.
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="glass-card overflow-hidden" data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between p-6 text-left group"
                data-testid={`faq-trigger-${i}`}
              >
                <span className="font-display text-lg text-white group-hover:text-emerald-300 transition-colors pr-4">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-zinc-400 flex-shrink-0 transition-transform ${open === i ? "rotate-180 text-emerald-300" : ""}`} />
              </button>
              {open === i && (
                <div className="px-6 pb-6 text-zinc-400 leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="relative py-32 px-6 lg:px-8">
    <div className="max-w-5xl mx-auto glass-panel rounded-3xl p-12 lg:p-20 text-center relative overflow-hidden">
      <div className="crystal-glow opacity-60" />
      <div className="relative">
        <SectionLabel>Free during MVP</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter text-white mb-6">
          Mine real leads. Draft real outreach.
        </h2>
        <p className="text-zinc-400 max-w-xl mx-auto mb-10 text-lg">
          Sign up, set an ICP, and pull your first verified nonprofit records straight from public 990 filings in under a minute.
        </p>
        <Link to="/register" className="btn-primary text-base" data-testid="final-cta">
          Get started <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="relative py-16 px-6 lg:px-8 border-t border-white/5">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Gem className="w-5 h-5 text-emerald-300" />
          <span className="font-display text-lg text-white">My Leads Convert</span>
        </div>
        <p className="text-sm text-zinc-500 max-w-sm">Real nonprofit prospecting, grounded outreach. Data sourced from public IRS 990 filings via ProPublica.</p>
      </div>
      <div className="flex items-center gap-6 text-sm text-zinc-500 flex-wrap">
        <a href="#features" className="hover:text-white transition-colors">Product</a>
        <a href="#how" className="hover:text-white transition-colors">How it works</a>
        <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
        <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
        <span>© 2026 My Leads Convert</span>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Navbar />
      <Hero />
      <HowItWorks />
      <Features />
      <DataSource />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
